<template>
  <BaseCheckboxSelector
    v-if="locationsMapped"
    :color="medium"
    data-cy="location-checkbox-selector"
    :disabled="disabled"
    :error-message="errorMessage"
    :items="locationsMapped"
    :label="$t('location_checkbox_selector.location')"
    :model-value="value"
    sticky-header
    @update:model-value="updateModelValue"
  >
    <template #item-description="{ selected, totalChildrenSelected }">
      {{ itemDescription(selected, totalChildrenSelected) }}
    </template>
  </BaseCheckboxSelector>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import { getLocalePluralText, getLocaleText } from "shared/boot/i18n";
import BaseCheckboxSelector from "shared/components/selectors/checkbox/BaseCheckboxSelector.vue";
import useLocations from "shared/composables/useLocations";

const props = defineProps({
  medium: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
  },
  required: {
    type: Boolean,
  },
  valid: {
    type: Boolean,
  },
  organisation: {
    type: [Object, null],
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "update:valid"]);

const { topLocations, locationsForParent } = useLocations(props.organisation);

const value = ref(props.modelValue);

const hasSelectedLocations = computed(() => value.value.length > 0);

const errorMessage = computed(() => {
  if (props.required && !hasSelectedLocations.value) {
    return getLocaleText(
      "location_checkbox_selector.select_at_least_one_location"
    );
  }

  return "";
});

function getChildren(parent) {
  if (parent.type === "Global") {
    return [];
  }

  return locationsForParent(parent).map((location) => ({
    id: location.id,
    name: location.name,
  }));
}

const locationsMapped = computed(() =>
  topLocations.value.map((parent) => {
    const children = getChildren(parent);

    return {
      id: parent.id,
      name: parent.name,
      children,
    };
  })
);

function itemDescription(isSelected, totalChildrenSelected) {
  if (!isSelected && !totalChildrenSelected) {
    return "";
  }

  let description = "• ";

  if (isSelected && totalChildrenSelected) {
    description += getLocalePluralText(
      "location_checkbox_selector.item_description.is_selected_and_children_selected",
      totalChildrenSelected,
      { totalChildrenSelected }
    );
  } else if (isSelected) {
    description += getLocaleText(
      "location_checkbox_selector.item_description.is_selected"
    );
  } else if (totalChildrenSelected) {
    description += getLocalePluralText(
      "location_checkbox_selector.item_description.children_selected",
      totalChildrenSelected,
      { totalChildrenSelected }
    );
  }

  return description;
}

function updateModelValue(items) {
  value.value = items;
  emit("update:modelValue", items);
}

watch(
  hasSelectedLocations,
  (val) => {
    if (props.required) {
      emit("update:valid", val);
    }
  },
  { immediate: true }
);

defineExpose({
  itemDescription,
});
</script>
